import { useEffect, useRef, useState } from "react";
import { Button, Icon, Spin, Text, UploadButton, useToast } from "@app/ui";
import { ErrorAlert, useErrorText } from "../errors/index.js";
import {
  deleteProductMedia,
  fetchProductMediaRaw,
  listProductMedia,
  uploadProductMedia,
} from "../api/dpp.js";
import type { ProductMediaItem } from "../api/dpp.js";
import { useT } from "../i18n.js";

/** Server-side rules mirrored for fast client feedback (the API re-enforces both). */
const ACCEPTED = "image/jpeg,image/png,image/webp";
const MAX_BYTES = 5 * 1024 * 1024;

/**
 * Product gallery manager. Thumbnails go through the authenticated raw route
 * (blob object-URLs) because the public /media/ URL only resolves after the
 * product has a published passport — the admin needs previews before that.
 */
export function MediaCard({
  orgId,
  productId,
  canWrite,
}: {
  orgId: string;
  productId: string;
  canWrite: boolean;
}) {
  const t = useT();
  const toast = useToast();
  const errorText = useErrorText();
  const [items, setItems] = useState<ProductMediaItem[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const urlsRef = useRef<Record<string, string>>({});

  const load = async () => {
    setError(null);
    try {
      // Component-card images are managed from the components editor — this
      // card owns the public gallery only.
      const list = (await listProductMedia(orgId, productId)).filter((m) => m.kind === "gallery");
      setItems(list);
      for (const m of list) {
        if (urlsRef.current[m.id]) continue;
        const blob = await fetchProductMediaRaw(orgId, productId, m.id);
        const url = URL.createObjectURL(blob);
        urlsRef.current[m.id] = url;
        setThumbs((prev) => ({ ...prev, [m.id]: url }));
      }
    } catch (e) {
      setError(e);
    }
  };

  useEffect(() => {
    void load();
    const urls = urlsRef.current;
    return () => {
      for (const url of Object.values(urls)) URL.revokeObjectURL(url);
      urlsRef.current = {};
    };
  }, [orgId, productId]);

  const upload = async (file: File) => {
    if (file.size > MAX_BYTES) {
      toast.error(t("media.tooLarge"));
      return;
    }
    setBusy(true);
    try {
      await uploadProductMedia(orgId, productId, file);
      toast.success(t("media.uploaded"));
      await load();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (mediaId: string) => {
    try {
      await deleteProductMedia(orgId, productId, mediaId);
      const url = urlsRef.current[mediaId];
      if (url) {
        URL.revokeObjectURL(url);
        delete urlsRef.current[mediaId];
        setThumbs((prev) => {
          const next = { ...prev };
          delete next[mediaId];
          return next;
        });
      }
      toast.success(t("media.deleted"));
      await load();
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  if (error != null) return <ErrorAlert error={error} />;
  if (items === null) return <Spin />;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--az-sp-4)" }}>
      {canWrite && (
        <div style={{ display: "flex", alignItems: "center", gap: "var(--az-sp-3)" }}>
          <UploadButton
            accept={ACCEPTED}
            size="sm"
            disabled={busy}
            icon={<Icon name="plus" size={14} />}
            onSelect={upload}
          >
            {t("media.upload")}
          </UploadButton>
          <Text secondary>{t("media.hint")}</Text>
        </div>
      )}
      {items.length === 0 ? (
        <Text secondary>{t("media.empty")}</Text>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
            gap: "var(--az-sp-4)",
          }}
        >
          {items.map((m) => (
            <div key={m.id} style={{ display: "flex", flexDirection: "column", gap: "var(--az-sp-2)" }}>
              {thumbs[m.id] ? (
                <img
                  src={thumbs[m.id]}
                  alt={m.alt ?? ""}
                  style={{
                    width: "100%",
                    height: 140,
                    objectFit: "cover",
                    borderRadius: "var(--az-radius, 6px)",
                    border: "1px solid var(--az-border, #ddd)",
                  }}
                />
              ) : (
                <Spin />
              )}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Text secondary>{Math.round(m.byteSize / 1024)} KB</Text>
                {canWrite && (
                  <Button size="sm" variant="danger" onClick={() => void remove(m.id)}>
                    {t("common.remove")}
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
